// FUNCTIONALITY-101/102 / CROSSSELL-109 / FUNCTIONALITY-111 — Store V2 cart.
import { useState } from "react";
import type { CartV2Item } from "./cart-v2.js";
import { computeTotals } from "./cart-v2.js";
import { formatUah } from "./money-v2.js";
import { EmptyState } from "./components.js";
import type { CatalogV2Product } from "./catalog-v2-types.js";
import type { CrossSellCandidate } from "./cross-sell-rules.js";
import { CanonicalThreeProductRecommendations } from "./CanonicalThreeProductRecommendations.js";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { t } from "./store-v2-ui-copy.js";
import { CartProductFinderV2, type CartAddSearchHit } from "./CartProductFinderV2.js";
import { miniImageUrl } from "./product-media-v2.js";

export type { CartAddSearchHit };

export interface CartV2ViewProps {
  lang: StoreV2Lang;
  items: CartV2Item[];
  promoCode: string | null;
  recommendations: CrossSellCandidate[];
  onInc: (id: string) => void;
  onDec: (id: string) => void;
  onRemove: (id: string) => void;
  onApplyPromo: (code: string) => void;
  onRemovePromo: () => void;
  onCheckout: () => void;
  onContinue: () => void;
  onOpenProduct: (productId: string, seed?: CatalogV2Product) => void;
  onAddRecommendation: (product: CatalogV2Product) => void;
  onRegisterActionable?: (products: CatalogV2Product[]) => void;
  onRecommendationsView?: (ids: string[]) => void;
  onRecommendationsRefresh?: () => void;
  canRefreshRecommendations?: boolean;
  recommendationsRefreshing?: boolean;
  recommendationsLoading?: boolean;
  /** CATALOG-SYNC-119A-R1 — clear all items via confirmation sheet. */
  onClearCart?: () => void;
  onClearCartRequest?: () => void;
  onClearCartCancel?: () => void;
  onAddSearchHit?: (hit: CartAddSearchHit) => void;
  reorderBanner?: string | null;
  reorderUnavailable?: Array<{ title: string; sku?: string | null }>;
  reorderCommentHint?: string | null;
}

export function CartV2View({
  lang,
  items,
  promoCode,
  recommendations,
  onInc,
  onDec,
  onRemove,
  onApplyPromo,
  onRemovePromo,
  onCheckout,
  onContinue,
  onOpenProduct,
  onAddRecommendation,
  onRegisterActionable,
  onRecommendationsView,
  onRecommendationsRefresh,
  canRefreshRecommendations = false,
  recommendationsRefreshing = false,
  recommendationsLoading = false,
  onClearCart,
  onClearCartRequest,
  onClearCartCancel,
  onAddSearchHit,
  reorderBanner,
  reorderUnavailable,
  reorderCommentHint,
}: CartV2ViewProps) {
  const totals = computeTotals(items, promoCode);
  const [code, setCode] = useState(promoCode ?? "");
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearedToast, setClearedToast] = useState(false);

  if (items.length === 0) {
    return (
      <div className="sv2-body">
        {reorderBanner ? (
          <div className="sv2-alert sv2-alert-ok" style={{ marginBottom: 12 }} data-testid="sv2-reorder-banner">
            {reorderBanner}
          </div>
        ) : null}
        {reorderUnavailable && reorderUnavailable.length > 0 ? (
          <div className="sv2-alert" style={{ marginBottom: 12 }} data-testid="sv2-reorder-unavailable">
            {t(lang, "reorder_item_unavailable")}
            <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
              {reorderUnavailable.map((row) => (
                <li key={`${row.title}-${row.sku ?? ""}`}>{row.title}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <EmptyState emoji="🛒" title={t(lang, "empty_cart")} hint={t(lang, "empty_cart_hint")} />
        {clearedToast ? (
          <div className="sv2-alert sv2-alert-ok" style={{ marginBottom: 12 }} data-sv2-cart-cleared="true">
            {t(lang, "cart_cleared")}
          </div>
        ) : null}
        <button type="button" className="sv2-cta sv2-secondary" data-sv2-btn-variant="secondary" onClick={onContinue}>
          {t(lang, "to_catalog")}
        </button>
        {onAddSearchHit ? (
          <CartProductFinderV2 lang={lang} items={items} onAdd={onAddSearchHit} />
        ) : null}
      </div>
    );
  }

  const applied = totals.promoStatus === "applied";
  const unavailableItems = items.filter(
    (i) =>
      i.availabilityLabel === "unavailable" ||
      String(i.stockStatus ?? "").toLowerCase() === "out_of_stock",
  );
  const availableItems = items.filter((i) => !unavailableItems.includes(i));

  return (
    <div className="sv2-body">
      <div className="sv2-section-title">{t(lang, "cart")}</div>
      {reorderBanner ? (
        <div className="sv2-alert sv2-alert-ok" style={{ marginBottom: 12 }} data-testid="sv2-reorder-banner">
          {reorderBanner}
        </div>
      ) : null}
      {reorderUnavailable && reorderUnavailable.length > 0 ? (
        <div className="sv2-alert" style={{ marginBottom: 12 }} data-testid="sv2-reorder-unavailable">
          {t(lang, "reorder_item_unavailable")}
          <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
            {reorderUnavailable.map((row) => (
              <li key={`${row.title}-${row.sku ?? ""}`}>{row.title}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {unavailableItems.length > 0 ? (
        <div className="sv2-alert" style={{ marginBottom: 12 }} data-testid="sv2-recovery-unavailable">
          {lang === "en" ? "Some items are temporarily unavailable." : "Деякі позиції тимчасово відсутні."}
          <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
            {unavailableItems.map((row) => (
              <li key={row.id}>
                {row.title}{" "}
                <button type="button" className="sv2-linkish" onClick={() => onRemove(row.id)}>
                  {lang === "en" ? "Remove" : "Прибрати"}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {reorderCommentHint ? (
        <p className="sv2-muted" style={{ margin: "0 0 12px", fontSize: 13 }}>
          {reorderCommentHint}
        </p>
      ) : null}

      {availableItems.map((item) => (
        <div key={item.id} className="sv2-cart-item">
          <button type="button" className="sv2-cart-thumb-btn" onClick={() => onOpenProduct(item.id)}>
            {item.imageUrl ? (
              <img className="sv2-cart-thumb" src={miniImageUrl(item.imageUrl) ?? item.imageUrl} alt={item.title} loading="lazy" />
            ) : (
              <div
                className="sv2-cart-thumb"
                style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                🍜
              </div>
            )}
          </button>
          <div className="sv2-cart-mid">
            <button type="button" className="sv2-cart-name-btn" onClick={() => onOpenProduct(item.id)}>
              {item.title}
            </button>
            <div className="sv2-cart-price">
              {formatUah(item.priceUah)} × {item.qty} = {formatUah(item.priceUah * item.qty)}
            </div>
            <div className="sv2-qty">
              <button type="button" className="sv2-qtybtn" onClick={() => onDec(item.id)} aria-label="−">
                −
              </button>
              <span className="sv2-qty-val">{item.qty}</span>
              <button type="button" className="sv2-qtybtn" onClick={() => onInc(item.id)} aria-label="+">
                ＋
              </button>
              <button
                type="button"
                className="sv2-del"
                onClick={() => onRemove(item.id)}
                style={{ marginLeft: "auto" }}
              >
                {t(lang, "delete")}
              </button>
            </div>
          </div>
        </div>
      ))}

      {onAddSearchHit ? (
        <CartProductFinderV2 lang={lang} items={items} onAdd={onAddSearchHit} />
      ) : null}

      <CanonicalThreeProductRecommendations
        lang={lang}
        items={recommendations.slice(0, 3)}
        onAdd={onAddRecommendation}
        onOpen={(p) => onOpenProduct(p.id, p)}
        {...(onRegisterActionable ? { onRegisterActionable } : {})}
        placement="CART_CROSS_SELL"
        onRefresh={() => onRecommendationsRefresh?.()}
        canRefresh={canRefreshRecommendations}
        refreshing={recommendationsRefreshing}
        loading={recommendationsLoading}
        {...(onRecommendationsView ? { onImpression: onRecommendationsView } : {})}
      />

      <div className="sv2-promo">
        <div className="sv2-promo-title">{t(lang, "promo")}</div>
        {applied ? (
          <div className="sv2-promo-applied">
            <span>
              {t(lang, "applied")} <strong>{totals.promoCode}</strong> · −{formatUah(totals.discountUah)}
            </span>
            <button type="button" className="sv2-promo-remove" onClick={onRemovePromo}>
              {t(lang, "remove")}
            </button>
          </div>
        ) : (
          <div className="sv2-promo-row">
            <input
              className="sv2-promo-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t(lang, "promo_placeholder")}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              onKeyDown={(e) => {
                if (e.key === "Enter" && code.trim()) onApplyPromo(code.trim());
              }}
            />
            <button
              type="button"
              className="sv2-promo-apply"
              disabled={!code.trim()}
              onClick={() => onApplyPromo(code.trim())}
            >
              {t(lang, "apply")}
            </button>
          </div>
        )}
        {totals.promoStatus === "invalid" ? (
          <div className="sv2-promo-msg sv2-promo-err">{t(lang, "promo_invalid")}</div>
        ) : null}
        {totals.promoStatus === "under_min" ? (
          <div className="sv2-promo-msg sv2-promo-err">
            {t(lang, "promo_under_min", { min: formatUah(totals.minOrderUah) })}
          </div>
        ) : null}
      </div>

      {/* R5C-R2 — summary + actions after cross-sell in normal document flow (no sticky/fixed). */}
      <div className="sv2-cart-actions" data-testid="sv2-cart-actions">
        <div className="sv2-summary" data-testid="sv2-cart-summary">
          <div className="sv2-row">
            <span>{t(lang, "subtotal")}</span>
            <span>{formatUah(totals.subtotalUah)}</span>
          </div>
          {totals.discountUah > 0 ? (
            <div className="sv2-row">
              <span>{t(lang, "discount")}</span>
              <span>−{formatUah(totals.discountUah)}</span>
            </div>
          ) : null}
          <div className="sv2-row sv2-row-total">
            <span>{t(lang, "total")}</span>
            <span>{formatUah(totals.totalUah)}</span>
          </div>
        </div>

        <button
          type="button"
          className="sv2-cta sv2-primary-commerce"
          data-testid="sv2-cart-checkout"
          onClick={onCheckout}
        >
          {t(lang, "checkout_cta")}
        </button>
        <button
          type="button"
          className="sv2-cta sv2-secondary sv2-cart-continue"
          data-testid="sv2-cart-continue"
          data-sv2-btn-variant="secondary"
          onClick={onContinue}
        >
          {t(lang, "continue_shopping")}
        </button>
        {onClearCart ? (
          <button
            type="button"
            className="sv2-cta sv2-clear-cart"
            data-testid="sv2-cart-clear"
            data-sv2-clear-cart="true"
            data-sv2-btn-variant="danger"
            onClick={() => {
              onClearCartRequest?.();
              setConfirmClear(true);
            }}
          >
            {t(lang, "clear_cart")}
          </button>
        ) : null}
      </div>

      {confirmClear ? (
        <div className="sv2-sheet-overlay" data-sv2-clear-cart-sheet="true" role="dialog" aria-modal="true">
          <button
            type="button"
            className="sv2-sheet-backdrop"
            aria-label={t(lang, "cancel")}
            onClick={() => {
              onClearCartCancel?.();
              setConfirmClear(false);
            }}
          />
          <div className="sv2-sheet-panel">
            <div className="sv2-sheet-handle" aria-hidden="true" />
            <div className="sv2-section-title">{t(lang, "clear_cart_title")}</div>
            <p className="sv2-muted" style={{ marginBottom: 16 }}>
              {t(lang, "clear_cart_body")}
            </p>
            <button
              type="button"
              className="sv2-cta sv2-secondary"
              onClick={() => {
                onClearCartCancel?.();
                setConfirmClear(false);
              }}
            >
              {t(lang, "cancel")}
            </button>
            <button
              type="button"
              className="sv2-cta sv2-clear-cart-confirm"
              style={{ marginTop: 8 }}
              onClick={() => {
                if (!onClearCart) return;
                setClearedToast(true);
                setConfirmClear(false);
                onClearCart();
                window.setTimeout(() => setClearedToast(false), 1800);
              }}
            >
              {t(lang, "clear_cart_confirm")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

